import { create } from 'zustand'
import { persist } from 'zustand/middleware'

/**
 * Supported UI languages. `en` is intentionally omitted — it lives in i18n
 * resources only as a developer-side fallback and is not user-selectable.
 * See architecture §8.1: "Default lang: ua (legacy), fallback: ru".
 */
export type Lang = 'ua' | 'ru'
export type Theme = 'light' | 'dark'

export const DEFAULT_LANG: Lang = 'ua'
export const DEFAULT_THEME: Theme = 'light'

export interface UIState {
  theme: Theme
  lang: Lang
  sidebarCollapsed: boolean
  /**
   * Set by feature forms (Settings, project-V2 in E5+) when the user has
   * unsaved input. Read by `WorkspaceSwitcher` (E02.07) and any future
   * destructive nav action that wants to prompt before discarding work.
   * Session-local — never persisted (a refresh wipes any pending input).
   */
  isDirty: boolean
  setTheme: (t: Theme) => void
  setLang: (l: Lang) => void
  toggleSidebar: () => void
  setDirty: (v: boolean) => void
}

/**
 * Sync the active theme to `<html data-theme="...">` so CSS-variable based
 * theming (Tailwind v4 + shadcn) updates without a reload. SSR-safe.
 */
function applyThemeToDom(theme: Theme): void {
  if (typeof document === 'undefined') return
  document.documentElement.setAttribute('data-theme', theme)
}

/**
 * Client-only UI state: theme, language, sidebar collapse.
 *
 * Persistence (story AC-09):
 *   - Key: `creo-ui` (zustand persist JSON envelope).
 *   - `partialize` keeps `theme` + `lang`. `sidebarCollapsed` is session-local
 *     (matches legacy frontend behavior).
 *   - One-shot legacy migration in `onRehydrateStorage`:
 *       * raw `theme` key  → state.theme  + remove legacy key
 *       * raw `lang`  key  → state.lang   + remove legacy key
 *     The legacy keys held bare strings (e.g. `localStorage.lang === 'ua'`),
 *     including the historical `uk` value for Ukrainian which we normalize
 *     to `ua` to match backend `notification_i18n.t()`.
 *
 * Note on i18n coupling (`src/i18n/index.ts`): the legacy `lang` key was
 * also read directly by `i18next-browser-languagedetector` via
 * `lookupLocalStorage: 'lang'`. After this migration runs once, the source
 * of truth becomes `useUIStore.getState().lang` (passed to i18n via `lng:`)
 * and the detector chain only matters for first-time visitors with no
 * stored preference.
 */
export const useUIStore = create<UIState>()(
  persist(
    (set) => ({
      theme: DEFAULT_THEME,
      lang: DEFAULT_LANG,
      sidebarCollapsed: false,
      isDirty: false,
      setTheme: (t) => {
        applyThemeToDom(t)
        set({ theme: t })
      },
      setLang: (l) => set({ lang: l }),
      toggleSidebar: () =>
        set((s) => ({ sidebarCollapsed: !s.sidebarCollapsed })),
      setDirty: (v) => set({ isDirty: v }),
    }),
    {
      name: 'creo-ui',
      partialize: (s) => ({ theme: s.theme, lang: s.lang }),
      onRehydrateStorage: () => (state) => {
        if (typeof window === 'undefined' || !state) return

        // Legacy migration: raw `theme` key.
        const legacyTheme = window.localStorage.getItem('theme')
        if (legacyTheme === 'light' || legacyTheme === 'dark') {
          state.theme = legacyTheme
        }
        if (legacyTheme !== null) {
          window.localStorage.removeItem('theme')
        }

        // Legacy migration: raw `lang` key (accept legacy `uk` → `ua`).
        const legacyLang = window.localStorage.getItem('lang')
        if (legacyLang === 'ua' || legacyLang === 'ru') {
          state.lang = legacyLang
        } else if (legacyLang === 'uk') {
          state.lang = 'ua'
        }
        if (legacyLang !== null) {
          window.localStorage.removeItem('lang')
        }

        // Sync DOM after rehydration so the first paint uses the persisted
        // theme without a flash of light-mode content.
        applyThemeToDom(state.theme)
      },
    },
  ),
)
